import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Label, Spinner, Table } from "reactstrap";

import { downloadBlob } from "../../../utils/downloadBlob";
import {
  baixarManifesto,
  baixarNotfis,
  desvincularEnvios,
  despacharLote,
  fecharLote,
  obterLote,
  reenviarEnviosDoLote,
  solicitarColetaLote,
} from "../../../services/despachoApi";
import { lerErroBlob } from "../../../services/pedidosApi";
import {
  Aviso,
  CheckboxTodos,
  EstadoTabela,
  ModalHub,
  Pill,
  SelecaoResumo,
  extractError,
  fieldInputStyle,
  fmtDataHora,
  fmtNumber,
  useSelecao,
} from "../_documentosFrete/comum";
import {
  apenasFalhas,
  hojeIso,
  podeDespachar,
  podeFechar,
  podeMexerEnvios,
  podeSolicitarColeta,
  rotuloOrigem,
  rotuloStatusEnvio,
  rotuloStatusLote,
  textoColeta,
  textoDespacho,
  textoDesvinculo,
  textoReenvio,
  tomStatusEnvio,
  tomStatusLote,
} from "./regras";

// Detalhe do lote (PO1-2377): envios com seleção para Desvincular (perguntando
// se também cancela a etiqueta na transportadora) e Reenviar (só os com falha,
// confirmando a nova cobrança); Fechar / Despachar (com pedido de coleta);
// Solicitar coleta de lote já despachado; downloads do manifesto (CSV) e do
// NOTFIS (txt). As confirmações acontecem na própria tela, como no Romaneio.
// Botões de escrita só com hub.operacao.despacho.editar (prop podeEditar).

export const TEXTO_COBRANCA_REENVIO = "Reenviar gera nova etiqueta e nova cobrança na transportadora.";

// Erros do módulo: { error } no JSON, ou o mesmo JSON dentro de um Blob quando o
// request pediu responseType blob (downloads), senão o 409/404 virava genérico.
async function mensagemDeErro(e, fallback) {
  const corpo = await lerErroBlob(e);
  if (corpo && typeof corpo === "object" && (corpo.error || corpo.message)) return corpo.error || corpo.message;
  return extractError(e, fallback);
}

const fmtPesoKg = (v) => (v === null || v === undefined || v === "" ? "-" : `${String(Number(v).toFixed(2)).replace(".", ",")} kg`);

export default function LoteDetalheModal({ idLote, podeEditar, onClose }) {
  const [lote, setLote] = useState(null);
  const [envios, setEnvios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState("");
  const [aviso, setAviso] = useState(null); // { tom, texto }
  const [ocupado, setOcupado] = useState(""); // ação em curso
  const [alterou, setAlterou] = useState(false);
  const [confirmacao, setConfirmacao] = useState(null); // 'desvincular' | 'reenviar' | 'despachar' | 'fechar' | 'coleta'
  const [cancelarEnvio, setCancelarEnvio] = useState(false);
  const [solicitarColeta, setSolicitarColeta] = useState(false);
  const [dataColeta, setDataColeta] = useState(hojeIso);
  const selecao = useSelecao("peso", "valor");

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga("");
    try {
      const r = await obterLote(idLote);
      setLote(r.lote || null);
      // useSelecao guarda por `id`: o envio é identificado pelo idEnvio.
      setEnvios((r.envios || []).map((e) => ({ ...e, id: e.idEnvio })));
    } catch (e) {
      setErroCarga(extractError(e, "Não foi possível carregar o lote."));
    } finally {
      setCarregando(false);
    }
  }, [idLote]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Seleção mudou: a confirmação era dos envios marcados antes.
  const qtdSelecionados = selecao.ids.length;
  useEffect(() => {
    setConfirmacao((atual) => (atual === "desvincular" || atual === "reenviar" ? null : atual));
  }, [qtdSelecionados]);

  const mexeEnvios = podeEditar && podeMexerEnvios(lote);
  const selecionadosComFalha = apenasFalhas(selecao.selecionados);

  const executar = async (nome, fn) => {
    if (ocupado) return;
    setOcupado(nome);
    setAviso(null);
    try {
      await fn();
    } catch (e) {
      setAviso({ tom: "erro", texto: await mensagemDeErro(e, "Não foi possível concluir a ação.") });
    } finally {
      setOcupado("");
      setConfirmacao(null);
    }
  };

  const desvincular = () =>
    executar("desvincular", async () => {
      const r = await desvincularEnvios(idLote, selecao.ids, cancelarEnvio);
      setAviso({ tom: Array.isArray(r.falhasCancelamento) && r.falhasCancelamento.length ? "aviso" : "ok", texto: textoDesvinculo(r) });
      selecao.limpar();
      setAlterou(true);
      await carregar();
    });

  const reenviar = () =>
    executar("reenviar", async () => {
      const ids = selecionadosComFalha.map((e) => e.idEnvio);
      const r = await reenviarEnviosDoLote(idLote, ids);
      const falhou = (r.resultados || []).some((x) => !x.ok);
      setAviso({ tom: falhou ? "aviso" : "ok", texto: textoReenvio(r) });
      selecao.limpar();
      setAlterou(true);
      await carregar();
    });

  const fechar = () =>
    executar("fechar", async () => {
      const r = await fecharLote(idLote);
      setAviso({ tom: "ok", texto: `Lote ${(r.lote && r.lote.codigo) || (lote && lote.codigo) || ""} fechado.` });
      setAlterou(true);
      await carregar();
    });

  const despachar = () =>
    executar("despachar", async () => {
      const r = await despacharLote(idLote, { solicitarColeta, dataColeta });
      setAviso(textoDespacho(r.lote || lote, r.coleta));
      setAlterou(true);
      await carregar();
    });

  // Lote já despachado sem coleta (ou com coleta a refazer).
  const pedirColeta = () =>
    executar("coleta", async () => {
      const r = await solicitarColetaLote(idLote, dataColeta);
      setAviso(textoColeta(r.lote || lote, r.coleta));
      setAlterou(true);
      await carregar();
    });

  // Download com responseType blob: o erro 409/404 vem em JSON dentro do Blob,
  // e `executar` o lê com lerErroBlob em vez de mostrar "Request failed".
  const baixar = (nome, fn, mime) =>
    executar(nome, async () => {
      const { conteudo, nome: arquivo } = await fn(idLote, lote && lote.codigo);
      downloadBlob(conteudo, arquivo, mime);
    });

  const sair = () => {
    if (ocupado) return;
    onClose(alterou);
  };

  const codigo = (lote && lote.codigo) || `#${idLote}`;
  const titulo = `Lote de despacho ${codigo}`;

  return (
    <ModalHub titulo={titulo} icone="tim-icons icon-delivery-fast" onClose={sair} maxWidth={1080}>
      {erroCarga ? <Aviso tom="erro">{erroCarga}</Aviso> : null}
      {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}
      {carregando && !lote ? (
        <EstadoTabela loading textoCarregando="Carregando o lote..." />
      ) : lote ? (
        <>
          <dl className="despacho-lotes__resumo">
            <div>
              <dt>Transportadora</dt>
              <dd>{lote.transportadora || "-"}</dd>
            </div>
            <div>
              <dt>Situação</dt>
              <dd>
                <Pill tom={tomStatusLote(lote.status)}>{rotuloStatusLote(lote.status)}</Pill>
              </dd>
            </div>
            <div>
              <dt>Origem</dt>
              <dd>{rotuloOrigem(lote.origem)}</dd>
            </div>
            <div>
              <dt>Envios / pedidos</dt>
              <dd>
                {fmtNumber(lote.qtdEnvios)} / {fmtNumber(lote.qtdPedidos)}
              </dd>
            </div>
            <div>
              <dt>Criado em</dt>
              <dd>{fmtDataHora(lote.creationTime)}</dd>
            </div>
            <div>
              <dt>Fechado em</dt>
              <dd>{fmtDataHora(lote.datFechamento)}</dd>
            </div>
            <div>
              <dt>Despachado em</dt>
              <dd>{fmtDataHora(lote.datDespacho)}</dd>
            </div>
            <div>
              <dt>Coleta</dt>
              <dd>{lote.numeroColeta || "-"}</dd>
            </div>
          </dl>

          <div className="docs-frete__barra" style={{ marginBottom: "0.75rem" }}>
            <div className="docs-frete__acoes">
              <Button color="primary" size="sm" className="btn-simple" onClick={() => baixar("manifesto", baixarManifesto, "text/csv")} disabled={Boolean(ocupado)}>
                <i className="tim-icons icon-cloud-download-93" style={{ marginRight: 6 }} />
                Manifesto (CSV)
              </Button>
              <Button color="primary" size="sm" className="btn-simple" onClick={() => baixar("notfis", baixarNotfis, "text/plain")} disabled={Boolean(ocupado)}>
                <i className="tim-icons icon-cloud-download-93" style={{ marginRight: 6 }} />
                NOTFIS (txt)
              </Button>
            </div>
            {podeEditar ? (
              <div className="docs-frete__acoes">
                {podeFechar(lote) ? (
                  <Button color="warning" size="sm" onClick={() => setConfirmacao("fechar")} disabled={Boolean(ocupado) || confirmacao === "fechar"}>
                    Fechar lote
                  </Button>
                ) : null}
                {podeDespachar(lote) ? (
                  <Button color="success" size="sm" onClick={() => setConfirmacao("despachar")} disabled={Boolean(ocupado) || confirmacao === "despachar"}>
                    Despachar
                  </Button>
                ) : null}
                {podeSolicitarColeta(lote) ? (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => setConfirmacao("coleta")}
                    disabled={Boolean(ocupado) || confirmacao === "coleta"}
                    title={lote.numeroColeta ? `Refaz a coleta (a atual é a nº ${lote.numeroColeta})` : "Pede a coleta à transportadora para este lote já despachado"}
                  >
                    {lote.numeroColeta ? "Refazer coleta" : "Solicitar coleta"}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>

          {confirmacao === "coleta" ? (
            <Aviso tom="aviso" role="alertdialog">
              {lote.numeroColeta ? (
                <>
                  Refazer a coleta do lote <strong>{codigo}</strong>? A coleta atual é a nº {lote.numeroColeta}; a transportadora recebe um pedido novo.
                </>
              ) : (
                <>
                  Solicitar à transportadora a coleta do lote <strong>{codigo}</strong> ({fmtNumber(lote.qtdEnvios)} envio(s))?
                </>
              )}
              <div className="despacho-lotes__opcoes">
                <Label for="lote-coleta-data" style={{ marginBottom: 0 }}>
                  Data da coleta
                </Label>
                <Input
                  id="lote-coleta-data"
                  type="date"
                  bsSize="sm"
                  value={dataColeta}
                  onChange={(e) => {
                    const valor = e.target.value; // antes do setState (event pooling, React 16)
                    setDataColeta(valor);
                  }}
                  style={fieldInputStyle}
                />
              </div>
              <div className="docs-frete__acoes">
                <Button color="primary" size="sm" onClick={pedirColeta} disabled={Boolean(ocupado) || !dataColeta}>
                  {ocupado === "coleta" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  Confirmar coleta
                </Button>
                <Button color="default" size="sm" outline onClick={() => setConfirmacao(null)} disabled={Boolean(ocupado)}>
                  Voltar
                </Button>
              </div>
            </Aviso>
          ) : null}

          {confirmacao === "fechar" ? (
            <Aviso tom="aviso" role="alertdialog">
              Fechar o lote <strong>{codigo}</strong>? Depois de fechado, ele não recebe envios novos; o despacho vem em seguida.
              <div className="docs-frete__acoes">
                <Button color="warning" size="sm" onClick={fechar} disabled={Boolean(ocupado)}>
                  {ocupado === "fechar" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  Confirmar fechamento
                </Button>
                <Button color="default" size="sm" outline onClick={() => setConfirmacao(null)} disabled={Boolean(ocupado)}>
                  Voltar
                </Button>
              </div>
            </Aviso>
          ) : null}

          {confirmacao === "despachar" ? (
            <Aviso tom="aviso" role="alertdialog">
              Despachar o lote <strong>{codigo}</strong> ({fmtNumber(lote.qtdEnvios)} envio(s))?
              <div className="despacho-lotes__opcoes">
                <Label check for="lote-solicitar-coleta">
                  <input
                    id="lote-solicitar-coleta"
                    type="checkbox"
                    checked={solicitarColeta}
                    onChange={(e) => {
                      const marcado = e.target.checked; // antes do setState (event pooling, React 16)
                      setSolicitarColeta(marcado);
                    }}
                  />
                  Solicitar coleta à transportadora
                </Label>
                <Label for="lote-data-coleta" style={{ marginBottom: 0 }}>
                  Data da coleta
                </Label>
                <Input
                  id="lote-data-coleta"
                  type="date"
                  bsSize="sm"
                  value={dataColeta}
                  disabled={!solicitarColeta}
                  onChange={(e) => {
                    const valor = e.target.value;
                    setDataColeta(valor);
                  }}
                  style={fieldInputStyle}
                />
              </div>
              <div className="docs-frete__acoes">
                <Button color="success" size="sm" onClick={despachar} disabled={Boolean(ocupado) || (solicitarColeta && !dataColeta)}>
                  {ocupado === "despachar" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  Confirmar despacho
                </Button>
                <Button color="default" size="sm" outline onClick={() => setConfirmacao(null)} disabled={Boolean(ocupado)}>
                  Voltar
                </Button>
              </div>
            </Aviso>
          ) : null}

          {mexeEnvios ? (
            <SelecaoResumo totais={selecao.totais} rotulo="envio(s)" semValor>
              <Button color="danger" size="sm" className="btn-simple" onClick={() => setConfirmacao("desvincular")} disabled={!selecao.ids.length || Boolean(ocupado) || confirmacao === "desvincular"}>
                Desvincular
              </Button>
              <Button
                color="primary"
                size="sm"
                className="btn-simple"
                onClick={() => setConfirmacao("reenviar")}
                disabled={!selecionadosComFalha.length || Boolean(ocupado) || confirmacao === "reenviar"}
                title="Reenvia só os envios selecionados que estão com falha"
              >
                Reenviar{selecionadosComFalha.length ? ` (${selecionadosComFalha.length})` : ""}
              </Button>
            </SelecaoResumo>
          ) : null}

          {confirmacao === "reenviar" ? (
            <Aviso tom="aviso" role="alertdialog">
              Reenviar <strong>{fmtNumber(selecionadosComFalha.length)}</strong> envio(s) com falha do lote {codigo}? {TEXTO_COBRANCA_REENVIO}
              <div className="docs-frete__acoes">
                <Button color="primary" size="sm" onClick={reenviar} disabled={Boolean(ocupado)}>
                  {ocupado === "reenviar" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  Confirmar reenvio
                </Button>
                <Button color="default" size="sm" outline onClick={() => setConfirmacao(null)} disabled={Boolean(ocupado)}>
                  Voltar
                </Button>
              </div>
            </Aviso>
          ) : null}

          {confirmacao === "desvincular" ? (
            <Aviso tom="aviso" role="alertdialog">
              Desvincular <strong>{fmtNumber(selecao.ids.length)}</strong> envio(s) do lote {codigo}?
              <div className="despacho-lotes__opcoes">
                <Label check for="lote-cancelar-envio">
                  <input
                    id="lote-cancelar-envio"
                    type="checkbox"
                    checked={cancelarEnvio}
                    onChange={(e) => {
                      const marcado = e.target.checked;
                      setCancelarEnvio(marcado);
                    }}
                  />
                  Também cancelar o envio (etiqueta) na transportadora
                </Label>
              </div>
              <div className="docs-frete__acoes">
                <Button color="danger" size="sm" onClick={desvincular} disabled={Boolean(ocupado)}>
                  {ocupado === "desvincular" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  Confirmar desvínculo
                </Button>
                <Button color="default" size="sm" outline onClick={() => setConfirmacao(null)} disabled={Boolean(ocupado)}>
                  Voltar
                </Button>
              </div>
            </Aviso>
          ) : null}

          <EstadoTabela loading={carregando && !envios.length} vazio={!carregando && !envios.length} textoCarregando="Carregando os envios..." textoVazio="O lote não tem envios." />
          {envios.length ? (
            <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  {mexeEnvios ? (
                    <th className="is-sel">
                      <CheckboxTodos itens={envios} selecao={selecao} label="Selecionar todos os envios" />
                    </th>
                  ) : null}
                  <th>Nota fiscal</th>
                  <th>Pedido</th>
                  <th>Destinatário</th>
                  <th>Cidade/UF</th>
                  <th>Rastreio</th>
                  <th>Situação</th>
                  <th className="is-number">Volumes</th>
                  <th className="is-number">Peso</th>
                </tr>
              </thead>
              <tbody>
                {envios.map((e) => (
                  <tr key={e.idEnvio}>
                    {mexeEnvios ? (
                      <td className="is-sel">
                        <input type="checkbox" aria-label={`Selecionar envio da NF ${e.numero}`} checked={selecao.has(e.idEnvio)} onChange={() => selecao.toggle(e)} />
                      </td>
                    ) : null}
                    <td>
                      <strong>{e.numero}</strong>
                      {e.serie ? <small> série {e.serie}</small> : null}
                    </td>
                    <td>{e.pedido || "-"}</td>
                    <td>{e.destinatario || "-"}</td>
                    <td className="docs-frete__nowrap">{[e.cidade, e.uf].filter(Boolean).join("/") || "-"}</td>
                    <td className="docs-frete__nowrap">{e.codigoRastreio || "-"}</td>
                    <td>
                      <Pill tom={tomStatusEnvio(e.status)}>{rotuloStatusEnvio(e.status)}</Pill>
                      {e.erro ? <small className="despacho-lotes__erro">{e.erro}</small> : null}
                    </td>
                    <td className="is-number">{e.volumes === null || e.volumes === undefined ? "-" : fmtNumber(e.volumes)}</td>
                    <td className="is-number">{fmtPesoKg(e.peso)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </>
      ) : null}
      <div className="docs-frete__modal-rodape">
        <Button color="default" size="sm" outline onClick={sair} disabled={Boolean(ocupado)}>
          Fechar
        </Button>
      </div>
    </ModalHub>
  );
}
